"use client";

import dynamic from "next/dynamic";
import { usePathname } from "next/navigation";
import { useEffect, useState } from "react";
import { shouldRenderEmailCapturePopup } from "@/lib/public-popup-gate";
import {
  resolveLocationByHost,
  resolveLocationByPathname,
} from "@/lib/restaurant-platform";
import { useBrowserPublicHost } from "@/lib/use-browser-public-host";

const IncentivePopup = dynamic(
  () => import("@/components/incentive-popup").then((mod) => mod.IncentivePopup),
  { ssr: false },
);

const LegacyEmailCapturePopup = dynamic(
  () => import("@/components/email-capture-popup").then((mod) => mod.EmailCapturePopup),
  { ssr: false },
);

export function EmailCapturePopupGate({
  popupEnabled = true,
  initialPublicHost,
}: {
  popupEnabled?: boolean;
  initialPublicHost?: string;
}) {
  const pathname = usePathname();
  const browserPublicHost = useBrowserPublicHost(initialPublicHost);
  const [idleReady, setIdleReady] = useState(false);

  // Defer mounting the popup (and thus the dynamic chunk fetch that pulls in
  // framer-motion) until the browser is idle, so it never competes with LCP
  // paint. The popup's own trigger thresholds (8s + scroll on the homepage,
  // 5s + 30% elsewhere) leave ample headroom for the idle-deferred chunk to
  // load before it ever needs to display.
  useEffect(() => {
    if (typeof window === "undefined") return;

    let cancelled = false;
    const markReady = () => {
      if (!cancelled) setIdleReady(true);
    };

    const requestIdle = window.requestIdleCallback;
    if (typeof requestIdle === "function") {
      const handle = requestIdle(markReady, { timeout: 3000 });
      return () => {
        cancelled = true;
        window.cancelIdleCallback?.(handle);
      };
    }

    const handle = window.setTimeout(markReady, 1500);
    return () => {
      cancelled = true;
      window.clearTimeout(handle);
    };
  }, []);

  if (!idleReady) return null;
  if (
    !shouldRenderEmailCapturePopup({
      popupEnabled,
      pathname,
      publicHost: browserPublicHost,
    })
  ) {
    return null;
  }

  const publicLocation =
    resolveLocationByPathname(pathname) ?? resolveLocationByHost(browserPublicHost);

  if (
    publicLocation?.restaurantId === "tuscan-cove" ||
    publicLocation?.restaurantId === "roma-kitchen"
  ) {
    return <IncentivePopup popupEnabled={popupEnabled} />;
  }

  return <LegacyEmailCapturePopup popupEnabled={popupEnabled} />;
}
